import { useState, useMemo, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line,
} from 'recharts'
import {
  TrendingUp, TrendingDown, AlertTriangle,
  Lightbulb, RefreshCw, ArrowRight,
} from 'lucide-react'
import clsx from 'clsx'
import { Card, MetricCard, Badge, PageHeader, EmptyState } from '../components/Card'
import ErrorBoundary from '../components/ErrorBoundary'
import { useToast } from '../components/Toast'
import { layout } from '../utils/layout'
import { budgetOverview } from '../api/client'

// ── Types ────────────────────────────────────────────────────────────────────

type PacingStatus = 'on_track' | 'overspending' | 'underspending'

// ── Constants ────────────────────────────────────────────────────────────────

const PLATFORM_COLORS: Record<string, string> = {
  'Meta Ads': '#1877f2',
  'Google Ads': '#34a853',
  'TikTok Ads': '#ff0050',
  'LinkedIn Ads': '#0a66c2',
  'Twitter/X Ads': '#1da1f2',
}

const PIE_COLORS = ['#1877f2', '#34a853', '#ff0050', '#0a66c2', '#1da1f2']

const PACING_CONFIG: Record<PacingStatus, { label: string; color: string; icon: typeof TrendingUp; badge: 'green' | 'yellow' | 'red' }> = {
  on_track:       { label: 'On Track',       color: 'text-green-400',  icon: TrendingUp,   badge: 'green'  },
  overspending:   { label: 'Overspending',   color: 'text-red-400',    icon: TrendingUp,   badge: 'red'    },
  underspending:  { label: 'Underspending',   color: 'text-yellow-400', icon: TrendingDown, badge: 'yellow' },
}

// ── Loading Skeleton ─────────────────────────────────────────────────────────

function BudgetSkeleton() {
  return (
    <div className="p-6 space-y-6 animate-pulse" role="status" aria-label="Loading budget data">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-24 bg-slate-800/50 rounded-xl" />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="h-72 bg-slate-800/50 rounded-xl" />
        <div className="h-72 bg-slate-800/50 rounded-xl" />
      </div>
      <div className="h-64 bg-slate-800/50 rounded-xl" />
      <span className="sr-only">Loading budget intelligence data...</span>
    </div>
  )
}

// ── Main View ────────────────────────────────────────────────────────────────

function BudgetViewContent() {
  const toast = useToast()

  // Allocation sliders state (what-if scenario)
  const [allocations, setAllocations] = useState<Record<string, number>>({
    'Meta Ads': 32,
    'Google Ads': 26,
    'TikTok Ads': 13,
    'LinkedIn Ads': 19,
    'Twitter/X Ads': 10,
  })
  const [showOptimal, setShowOptimal] = useState(false)

  const { data: budgetData, isLoading, isError, refetch } = useQuery({
    queryKey: ['budget', 'overview'],
    queryFn: () => budgetOverview('30d'),
  })

  const allocationTotal = useMemo(
    () => Object.values(allocations).reduce((s, v) => s + v, 0),
    [allocations],
  )

  const handleAllocationChange = useCallback((platform: string, value: number) => {
    setAllocations(prev => ({ ...prev, [platform]: value }))
  }, [])

  const handleOptimalSplit = useCallback(() => {
    // Simulate optimal allocation based on ROI
    setAllocations({
      'Meta Ads': 28,
      'Google Ads': 22,
      'TikTok Ads': 25,
      'LinkedIn Ads': 15,
      'Twitter/X Ads': 10,
    })
    setShowOptimal(true)
    toast.info('Budget reallocated based on ROI optimization')
  }, [toast])

  if (isLoading) return <BudgetSkeleton />

  if (isError) {
    return (
      <div className="p-6">
        <Card>
          <EmptyState
            icon={<AlertTriangle className="w-12 h-12 text-red-400" />}
            title="Failed to load budget data"
            description="Could not fetch budget intelligence. Please try again."
            action={
              <button
                onClick={() => void refetch()}
                className="bg-brand-600 hover:bg-brand-700 text-white text-sm px-4 py-2 rounded-lg"
              >
                Retry
              </button>
            }
          />
        </Card>
      </div>
    )
  }

  const { platforms, totalBudget, totalSpent, remaining, pacing, alerts, pacingData, weightedRoi } = budgetData!
  const pacingCfg = PACING_CONFIG[pacing]
  const PacingIcon = pacingCfg.icon

  // Pie chart data for current allocation
  const pieData = platforms.map(p => ({
    name: p.platform,
    value: p.budget,
  }))

  // ROI comparison bar chart
  const roiData = platforms.map(p => ({
    platform: p.platform.replace(' Ads', ''),
    roi: p.roi,
    cpa: p.cpa,
    fill: p.color,
  }))

  return (
    <div>
      <PageHeader
        title="Budget Intelligence"
        subtitle="Cross-platform budget optimization and ROI analysis"
        action={
          <div className="flex items-center gap-2">
            <Badge color={pacingCfg.badge}>
              <PacingIcon className="w-3 h-3 mr-1" />
              {pacingCfg.label}
            </Badge>
            <button
              onClick={() => void refetch()}
              className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 transition-colors"
              aria-label="Refresh budget data"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        }
      />

      <div className={`${layout.pagePadding} ${layout.pageStack}`}>
        {/* Key Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            label="Total Budget"
            value={`$${totalBudget.toLocaleString()}`}
          />
          <MetricCard
            label="Total Spent"
            value={`$${totalSpent.toLocaleString()}`}
            change={-3.2}
          />
          <MetricCard
            label="Remaining"
            value={`$${remaining.toLocaleString()}`}
          />
          <MetricCard
            label="Weighted ROAS"
            value={`${weightedRoi.toFixed(1)}x`}
            change={0.3}
          />
        </div>

        {/* Budget Pacing + Allocation Pie */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Pacing Chart */}
          <Card title="Budget Pacing (30 days)">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={pacingData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#64748b' }} interval={4} />
                  <YAxis
                    tick={{ fontSize: 10, fill: '#64748b' }}
                    domain={[0, 110]}
                    tickFormatter={(v: number) => `${v}%`}
                  />
                  <Tooltip
                    contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }}
                    formatter={(v: number) => [`${v.toFixed(1)}%`, '']}
                  />
                  <Line
                    type="monotone"
                    dataKey="ideal"
                    stroke="#64748b"
                    strokeDasharray="4 4"
                    strokeWidth={1.5}
                    dot={false}
                    name="Ideal Pace"
                  />
                  <Line
                    type="monotone"
                    dataKey="actual"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    dot={false}
                    name="Actual Spend"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-4 mt-2 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <span className="w-3 h-0.5 bg-slate-500 inline-block" style={{ borderTop: '2px dashed #64748b' }} />
                Ideal Pace
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-0.5 bg-yellow-500 inline-block" />
                Actual Spend
              </span>
            </div>
          </Card>

          {/* Allocation Pie Chart */}
          <Card title="Budget Allocation">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }: { name: string; percent: number }) =>
                      `${name.replace(' Ads', '')} ${(percent * 100).toFixed(0)}%`
                    }
                    labelLine={{ stroke: '#64748b' }}
                  >
                    {pieData.map((_, idx) => (
                      <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }}
                    formatter={(v: number) => [`$${v.toLocaleString()}`, 'Budget']}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* ROI Comparison Chart */}
        <Card title="Per-Platform ROI Comparison">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={roiData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="platform" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={(v: number) => `${v}x`} />
                <Tooltip
                  contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }}
                  formatter={(v: number, name: string) => [
                    name === 'roi' ? `${v.toFixed(1)}x` : `$${v.toFixed(2)}`,
                    name === 'roi' ? 'ROAS' : 'CPA',
                  ]}
                />
                <Bar dataKey="roi" name="ROAS" radius={[4, 4, 0, 0]}>
                  {roiData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Platform Breakdown Table */}
        <Card title="Platform Breakdown">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" role="table" aria-label="Platform budget breakdown">
              <thead>
                <tr className="text-xs text-slate-500 border-b border-surface-border">
                  <th className="text-left pb-2 pr-4" scope="col">Platform</th>
                  <th className="text-right pb-2 pr-4" scope="col">Budget</th>
                  <th className="text-right pb-2 pr-4" scope="col">Spent</th>
                  <th className="text-right pb-2 pr-4" scope="col">Remaining</th>
                  <th className="text-right pb-2 pr-4" scope="col">ROAS</th>
                  <th className="text-right pb-2 pr-4" scope="col">CPA</th>
                  <th className="text-right pb-2" scope="col">Conversions</th>
                </tr>
              </thead>
              <tbody>
                {platforms.map(p => {
                  const pctSpent = (p.spent / p.budget) * 100
                  return (
                    <tr key={p.platform} className="border-b border-surface-border/50 hover:bg-slate-800/30">
                      <td className="py-2.5 pr-4">
                        <div className="flex items-center gap-2">
                          <span
                            className="w-2.5 h-2.5 rounded-full shrink-0"
                            style={{ backgroundColor: p.color }}
                            aria-hidden="true"
                          />
                          <span className="font-medium text-slate-200">{p.platform}</span>
                        </div>
                      </td>
                      <td className="py-2.5 pr-4 text-right text-slate-300">${p.budget.toLocaleString()}</td>
                      <td className="py-2.5 pr-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-1.5 bg-slate-800 rounded-full overflow-hidden" aria-hidden="true">
                            <div
                              className={clsx('h-full rounded-full', pctSpent > 85 ? 'bg-red-500' : pctSpent > 70 ? 'bg-yellow-500' : 'bg-green-500')}
                              style={{ width: `${Math.min(pctSpent, 100)}%` }}
                            />
                          </div>
                          <span className="text-slate-300">${p.spent.toLocaleString()}</span>
                        </div>
                      </td>
                      <td className="py-2.5 pr-4 text-right text-slate-400">
                        ${(p.budget - p.spent).toLocaleString()}
                      </td>
                      <td className="py-2.5 pr-4 text-right">
                        <span className={clsx(
                          'font-semibold',
                          p.roi >= 4 ? 'text-green-400' : p.roi >= 3 ? 'text-yellow-400' : 'text-red-400',
                        )}>
                          {p.roi.toFixed(1)}x
                        </span>
                      </td>
                      <td className="py-2.5 pr-4 text-right text-slate-300">${p.cpa.toFixed(2)}</td>
                      <td className="py-2.5 text-right text-slate-300">{p.conversions}</td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr className="text-xs text-slate-400 font-semibold">
                  <td className="pt-3">Total</td>
                  <td className="pt-3 text-right">${totalBudget.toLocaleString()}</td>
                  <td className="pt-3 text-right">${totalSpent.toLocaleString()}</td>
                  <td className="pt-3 text-right">${remaining.toLocaleString()}</td>
                  <td className="pt-3 text-right">{weightedRoi.toFixed(1)}x</td>
                  <td className="pt-3 text-right">--</td>
                  <td className="pt-3 text-right">{platforms.reduce((s, p) => s + p.conversions, 0)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>

        {/* Alerts + What-If Modeler */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Active Alerts */}
          <Card title={`Budget Alerts (${alerts.length})`}>
            {!alerts.length ? (
              <EmptyState
                icon={<AlertTriangle className="w-10 h-10" />}
                title="No active alerts"
                description="All budgets are within healthy parameters."
              />
            ) : (
              <div className="space-y-2">
                {alerts.map(alert => (
                  <div
                    key={alert.id}
                    className={clsx(
                      'flex items-start gap-3 p-3 rounded-lg border',
                      alert.severity === 'critical' ? 'bg-red-900/20 border-red-800' :
                      alert.severity === 'warning' ? 'bg-yellow-900/20 border-yellow-800' :
                      'bg-blue-900/20 border-blue-800',
                    )}
                    role="alert"
                  >
                    <AlertTriangle className={clsx(
                      'w-4 h-4 shrink-0 mt-0.5',
                      alert.severity === 'critical' ? 'text-red-400' :
                      alert.severity === 'warning' ? 'text-yellow-400' : 'text-blue-400',
                    )} aria-hidden="true" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs text-slate-400">{alert.platform}</span>
                        <Badge color={
                          alert.severity === 'critical' ? 'red' :
                          alert.severity === 'warning' ? 'yellow' : 'blue'
                        }>
                          {alert.type.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-300">{alert.message}</p>
                      <time className="text-[10px] text-slate-600 mt-1 block" dateTime={alert.timestamp}>
                        {new Date(alert.timestamp).toLocaleString('en', {
                          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                        })}
                      </time>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* What-If Scenario Modeler */}
          <Card
            title="What-If Scenario Modeler"
            action={
              <button
                onClick={handleOptimalSplit}
                className="flex items-center gap-1.5 text-xs bg-brand-600 hover:bg-brand-700 text-white px-2.5 py-1 rounded-md transition-colors"
                aria-label="Calculate optimal budget split"
              >
                <Lightbulb className="w-3.5 h-3.5" />
                Optimal Split
              </button>
            }
          >
            <div className="space-y-4">
              <p className="text-xs text-slate-500">
                Adjust allocation percentages to model different budget scenarios.
                Total should equal 100%.
              </p>

              {Object.entries(allocations).map(([platform, pct]) => (
                <div key={platform} className="flex items-center gap-3">
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: PLATFORM_COLORS[platform] ?? '#64748b' }}
                    aria-hidden="true"
                  />
                  <label className="text-xs text-slate-300 w-28 shrink-0" htmlFor={`alloc-${platform}`}>
                    {platform.replace(' Ads', '')}
                  </label>
                  <input
                    id={`alloc-${platform}`}
                    type="range"
                    min="0"
                    max="60"
                    value={pct}
                    onChange={(e) => handleAllocationChange(platform, Number(e.target.value))}
                    className="flex-1 accent-brand-500"
                    aria-label={`${platform} allocation: ${pct}%`}
                  />
                  <span className={clsx(
                    'text-xs w-10 text-right font-mono',
                    pct > 30 ? 'text-brand-300' : 'text-slate-400',
                  )}>
                    {pct}%
                  </span>
                </div>
              ))}

              <div className="flex items-center justify-between pt-3 border-t border-surface-border">
                <span className="text-xs text-slate-500">Total allocation</span>
                <span className={clsx(
                  'text-sm font-semibold',
                  allocationTotal === 100 ? 'text-green-400' :
                  Math.abs(allocationTotal - 100) <= 5 ? 'text-yellow-400' : 'text-red-400',
                )}>
                  {allocationTotal}%
                  {allocationTotal !== 100 && (
                    <span className="text-xs text-slate-500 ml-1">
                      ({allocationTotal > 100 ? '+' : ''}{allocationTotal - 100}%)
                    </span>
                  )}
                </span>
              </div>

              {showOptimal && (
                <div className="bg-brand-900/20 border border-brand-700 rounded-lg p-3 text-xs text-brand-200">
                  <div className="flex items-center gap-1.5 mb-1 font-medium">
                    <Lightbulb className="w-3.5 h-3.5" aria-hidden="true" />
                    Optimal Split Applied
                  </div>
                  <p className="text-slate-400">
                    Based on current ROI data, TikTok Ads allocation increased from 13% to 25%
                    due to highest ROAS (5.1x). LinkedIn and Meta allocations reduced proportionally.
                  </p>
                </div>
              )}

              {allocationTotal === 100 && (
                <div className="flex items-center gap-2">
                  <div className="flex-1 text-xs text-slate-500">
                    Projected monthly spend: ${totalBudget.toLocaleString()}
                  </div>
                  <button className="flex items-center gap-1.5 text-xs bg-green-700/60 hover:bg-green-700 text-green-100 px-3 py-1.5 rounded-md transition-colors">
                    Apply Allocation
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default function BudgetView() {
  return (
    <ErrorBoundary>
      <BudgetViewContent />
    </ErrorBoundary>
  )
}
